import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FiUpload, FiUsers, FiActivity, FiFileText, FiCheckCircle } from "react-icons/fi";
import useAuth from "../../../hooks/useAuth";
import bankService from "../../../services/bank.service";
import { readPagination } from "../../../utils/listPagination";
import "../../../styles/dashboard.css";
import "../../../styles/bankDashboard.css";

export default function BankDashboard() {
  const { auth } = useAuth();
  const user = auth?.user;
  const isAdmin = user?.role === "BANK_ADMIN";

  const [stats, setStats] = useState({
    records: 0,
    persons: 0,
    agencies: 0,
    files: 0,
    seized: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [recRes, tracRes, uploadRes, seizedRes] = await Promise.all([
          bankService.getRecords({ limit: 1 }),
          bankService.getTracingView(),
          bankService.listUploads(),
          bankService.getTraceCases({ limit: 1, view: "seized" }),
        ]);
        const records = recRes?.data?.data?.total || 0;
        const agencies = tracRes?.data?.data?.agencies?.length || 0;
        const files = uploadRes?.data?.data?.batches?.length || 0;
        const seized = readPagination(seizedRes?.data).total;

        let persons = 0;
        if (isAdmin) {
          const pRes = await bankService.getPersons();
          persons = pRes?.data?.data?.persons?.length || 0;
        }

        setStats({ records, persons, agencies, files, seized });
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [isAdmin]);

  return (
    <div className="page bank-dashboard">
      <div className="dashboard-header bank-dashboard__header">
        <div>
          <h2>Welcome, {user?.name}</h2>
          <p className="muted bank-dashboard__meta">
            {user?.bank?.bankName} &nbsp;·&nbsp; {user?.role === "BANK_ADMIN" ? "Bank Admin" : "Bank Person"}
          </p>
        </div>
      </div>

      {loading ? (
        <p className="muted">Loading...</p>
      ) : (
        <div className="bank-dashboard__grid bank-dashboard__stats">
          <StatCard label="Total Records" value={stats.records} icon={<FiUpload />} to="/bank/records" />
          <StatCard label="Uploaded Files" value={stats.files} icon={<FiFileText />} to="/bank/files" />
          <StatCard label="Linked Agencies" value={stats.agencies} icon={<FiActivity />} to="/bank/tracing" />
          <StatCard
            label="Total seized vehicles"
            value={stats.seized}
            icon={<FiCheckCircle />}
            to="/bank/seized-vehicles"
          />
          {isAdmin ? (
            <StatCard label="Persons" value={stats.persons} icon={<FiUsers />} to="/bank/persons" />
          ) : null}
        </div>
      )}

      <div className="bank-dashboard__grid bank-dashboard__actions">
        <ActionCard
          to="/bank/records"
          icon="📁"
          title="Upload / View Records"
          desc="Upload repo vehicle records"
        />
        <ActionCard
          to="/bank/trace-cases"
          icon="🚗"
          title="Trace Cases"
          desc="Search vehicle, customer, loan or bank"
        />
        <ActionCard
          to="/bank/seized-vehicles"
          icon="✅"
          title="Seized vehicles"
          desc="Bank-uploaded vehicles seized by linked agencies"
        />
        <ActionCard
          to="/bank/tracing"
          icon="📡"
          title="View Tracing"
          desc="See linked agencies and live tracing"
        />
        {isAdmin ? (
          <ActionCard
            to="/bank/persons"
            icon="👥"
            title="Manage Persons"
            desc="Add and manage bank users"
          />
        ) : null}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon, to }) {
  return (
    <Link to={to} className="bank-dashboard__link">
      <div className="bank-dashboard__stat">
        <span className="bank-dashboard__stat-icon">{icon}</span>
        <div className="bank-dashboard__stat-copy">
          <div className="bank-dashboard__stat-value">{value}</div>
          <div className="bank-dashboard__stat-label">{label}</div>
        </div>
      </div>
    </Link>
  );
}

function ActionCard({ to, icon, title, desc }) {
  return (
    <Link to={to} className="bank-dashboard__link">
      <div className="bank-dashboard__action">
        <div className="bank-dashboard__action-icon">{icon}</div>
        <h4>{title}</h4>
        <p>{desc}</p>
      </div>
    </Link>
  );
}
